import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Calendar, LogIn, Laptop } from 'lucide-react';
import { Button } from '../ui/button';
import { navigationLinks } from '../../data/homepage';
import LanguageSwitcher from '../common/LanguageSwitcher';
import logoDark from '../../assets/bmp-logo-dark.png';
import logoLight from '../../assets/bmp-logo-final-v2.png';

// Routes that open with a dark hero — header is transparent there until
// the user scrolls past the fold, then it morphs into the standard
// white/glass bar. Either an exact path match or a `startsWith` prefix
// (with trailing `/`) for dynamic detail routes.
const DARK_HERO_ROUTES = new Set([
  '/',
  '/about',
  '/services',
  '/projects',
  '/insights',
  '/contact',
  '/schedule',
  '/brand-studio',
  '/it-solutions',
]);
const DARK_HERO_PREFIXES = ['/projects/', '/insights/', '/services/', '/it-solutions/'];

const isDarkHeroRoute = (pathname) =>
  DARK_HERO_ROUTES.has(pathname) ||
  DARK_HERO_PREFIXES.some((p) => pathname.startsWith(p));

const NewHeader = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Transparent-on-dark only while the user is still on the hero. Once
  // they scroll OR navigate to a light-background route, snap back to
  // the readable white bar.
  const isDarkHero = isDarkHeroRoute(location.pathname) && !scrolled && !mobileMenuOpen;
  const headerBg = isDarkHero
    ? 'bg-transparent'
    : scrolled
      ? 'bg-white/95 backdrop-blur-md shadow-sm'
      : 'bg-white';
  const navLinkColor = isDarkHero ? 'text-white/90 hover:text-white' : 'text-[#2B2D42] hover:text-[#00B4D8]';
  const navLinkActive = isDarkHero ? 'text-white' : 'text-[#00B4D8]';
  const loginBtnColor = isDarkHero
    ? 'text-white hover:text-white hover:bg-white/10'
    : 'text-[#2B2D42] hover:text-[#00B4D8] hover:bg-transparent';
  const mobileBtnColor = isDarkHero ? 'text-white' : 'text-[#2B2D42] hover:text-[#00B4D8]';
  const currentLogo = isDarkHero ? logoLight : logoDark;

  return (
    <header
      data-testid="site-header"
      data-dark-hero={isDarkHero ? 'true' : 'false'}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img src={currentLogo} alt="Grupo BMP" className="h-14 md:h-16 w-auto transition-opacity duration-300" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigationLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '')}`}
                className={`text-base font-medium transition-colors duration-300 relative group ${
                  location.pathname === link.path ? navLinkActive : navLinkColor
                }`}
              >
                {link.i18nKey ? t(link.i18nKey) : link.name}
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 ${isDarkHero ? 'bg-white' : 'bg-[#00B4D8]'} transform origin-left transition-transform duration-300 ${
                    location.pathname === link.path ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-3">
            <Link
              to="/it-solutions"
              data-testid="navbar-it-solutions-cta"
              className="group relative inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12px] font-bold uppercase tracking-wider text-white bg-gradient-to-r from-[#023E8A] via-[#0F172A] to-[#00B4D8] shadow-md hover:shadow-lg hover:scale-[1.03] transition-all"
            >
              <Laptop className="w-3.5 h-3.5 text-amber-300 group-hover:rotate-[-6deg] transition-transform" />
              IT Solutions
            </Link>
            <LanguageSwitcher variant="ghost" showLabel={false} darkMode={isDarkHero} />
            <Link to="/admin/login">
              <Button
                variant="ghost"
                data-testid="navbar-login-btn"
                className={loginBtnColor}
              >
                <LogIn className="w-4 h-4 mr-2" />
                {t('homepage.nav.login')}
              </Button>
            </Link>
            <Link to="/schedule">
              <Button data-testid="navbar-schedule-btn" className="bg-[#00B4D8] hover:bg-[#48CAE4] text-white shadow-md hover:shadow-lg transition-all duration-300">
                <Calendar className="w-4 h-4 mr-2" />
                {t('homepage.nav.scheduleMeeting')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 transition-colors ${mobileBtnColor}`}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-[#E9ECEF]">
            <nav className="flex flex-col space-y-4">
              {navigationLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-base font-medium py-2 transition-colors ${
                    location.pathname === link.path
                      ? 'text-[#00B4D8]'
                      : 'text-[#2B2D42] hover:text-[#00B4D8]'
                  }`}
                >
                  {link.i18nKey ? t(link.i18nKey) : link.name}
                </Link>
              ))}
              <div className="pt-4 space-y-3">
                <Link to="/it-solutions" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-it-solutions-cta">
                  <Button className="w-full bg-gradient-to-r from-[#023E8A] via-[#0F172A] to-[#00B4D8] text-white font-bold uppercase tracking-wider text-xs">
                    <Laptop className="w-4 h-4 mr-2 text-amber-300" />
                    IT Solutions
                  </Button>
                </Link>
                <div className="flex justify-center mb-3">
                  <LanguageSwitcher variant="outline" showLabel={true} />
                </div>
                <Link to="/admin/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    variant="outline"
                    className="w-full border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8] hover:text-white"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    {t('homepage.nav.login')}
                  </Button>
                </Link>
                <Link to="/schedule" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#00B4D8] hover:bg-[#48CAE4] text-white">
                    <Calendar className="w-4 h-4 mr-2" />
                    {t('homepage.nav.scheduleMeeting')}
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default NewHeader;